import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { tokenIsExpired } from '../authUtils';

/**
 * Custom hook for managing user session and token validation
 * Provides utilities for checking session validity and handling token refresh
 */
const useSessionManager = () => {
    const { isAuthenticated, token, logout } = useAuth();
    const tokenCheckIntervalRef = useRef(null);

    // Check if current session is valid
    const isSessionValid = useCallback(() => {
        if (!isAuthenticated || !token) {
            return false;
        }

        return !tokenIsExpired(token);
    }, [isAuthenticated, token]);

    // Force logout if session becomes invalid
    const validateSession = useCallback(() => {
        if (!isSessionValid()) {
            console.log('Session validation failed, logging out...');
            logout();
            return false;
        }
        return true;
    }, [isSessionValid, logout]);

    // Get remaining session time in milliseconds
    const getSessionTimeRemaining = useCallback(() => {
        if (!token || !isAuthenticated) {
            return 0;
        }

        try {
            const { jwtDecode } = require('jwt-decode');
            const decodedToken = jwtDecode(token);
            const expirationTime = decodedToken.exp * 1000;
            const currentTime = Date.now();
            const remaining = expirationTime - currentTime;
            
            return Math.max(0, remaining);
        } catch (error) {
            console.error('Error calculating session time remaining:', error);
            return 0;
        }
    }, [token, isAuthenticated]);

    // Set up periodic session validation
    useEffect(() => {
        if (!isAuthenticated || !token) {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
                tokenCheckIntervalRef.current = null;
            }
            return;
        }

        // Validate session immediately
        if (!validateSession()) {
            return;
        }

        // Set up periodic validation every 30 seconds
        tokenCheckIntervalRef.current = setInterval(() => {
            validateSession();
        }, 30000);

        return () => {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
                tokenCheckIntervalRef.current = null;
            }
        };
    }, [isAuthenticated, token, validateSession]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (tokenCheckIntervalRef.current) {
                clearInterval(tokenCheckIntervalRef.current);
            }
        };
    }, []);

    return {
        isSessionValid: isSessionValid(),
        validateSession,
        getSessionTimeRemaining,
        isAuthenticated,
        token
    };
};

export default useSessionManager;